import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { videoLessonsApi, CreateVideoLessonDto, VideoLesson, VideoLessonType } from '../../../api/video-lessons';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { UseVideoLessonsReturn } from '../types';
import { getErrorMessage } from '@/lib/errors';

const initialFormData: CreateVideoLessonDto = {
  sort_id: 1,
  title: '',
  description: '',
  type: 'other',
  url: '',
  is_active: true,
};

const buildPayload = (formData: CreateVideoLessonDto): CreateVideoLessonDto => {
  const trimmedDescription = formData.description?.trim();
  return {
    ...formData,
    description: trimmedDescription ? trimmedDescription : null,
  };
};

export function useVideoLessons(): UseVideoLessonsReturn {
  const confirm = useConfirm();
  const [videoLessons, setVideoLessons] = useState<VideoLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<VideoLesson | null>(null);
  const [formData, setFormData] = useState<CreateVideoLessonDto>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchVideoLessons = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await videoLessonsApi.getAll();
      setVideoLessons(data);
    } catch (error: unknown) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить видеоуроки'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideoLessons();
  }, [fetchVideoLessons]);

  const handleCreate = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await videoLessonsApi.create(buildPayload(formData));
      toast.success('Видеоурок создан');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchVideoLessons();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось создать видеоурок'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Форма общая с редактированием: создание всегда начинается с чистых полей.
  const openCreateDialog = (open: boolean) => {
    if (open) setFormData(initialFormData);
    setIsCreateDialogOpen(open);
  };

  const handleEdit = (lesson: VideoLesson) => {
    setEditingLesson(lesson);
    setFormData({
      sort_id: lesson.sort_id,
      title: lesson.title,
      description: lesson.description || '',
      type: lesson.type as VideoLessonType,
      url: lesson.url,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingLesson || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await videoLessonsApi.update(editingLesson.id, buildPayload(formData));
      toast.success('Видеоурок обновлён');
      setIsEditDialogOpen(false);
      setEditingLesson(null);
      fetchVideoLessons();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось обновить видеоурок'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Удалить видеоурок?',
      description: 'Это действие необратимо.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;
    try {
      await videoLessonsApi.delete(id);
      toast.success('Видеоурок удалён');
      fetchVideoLessons();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось удалить видеоурок'));
    }
  };

  /** Быстрое скрытие/показ урока прямо из таблицы, без открытия формы. */
  const handleToggleActive = async (lesson: VideoLesson) => {
    try {
      await videoLessonsApi.update(lesson.id, { is_active: !lesson.is_active });
      setVideoLessons((prev) =>
        prev.map((item) =>
          item.id === lesson.id ? { ...item, is_active: !lesson.is_active } : item,
        ),
      );
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось изменить статус'));
    }
  };

  return {
    loadError,
    retryLoad: fetchVideoLessons,
    videoLessons,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingLesson,
    formData,
    isSubmitting,
    setIsCreateDialogOpen: openCreateDialog,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  };
}
